import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./lobby.css";
import { syncJoinLobbyFromUrlSearch } from "../../utils/joinLobbyFromUrl";
import { persistUserDataIds } from "../../functions/persistUserDataIds";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

function buildLobbyName() {
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `LOBBY-${rand}`;
}

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function normalizePlayerName(user) {
  if (!user || typeof user !== "object") return "";
  const raw = user.name;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t && !/^null(\s+null)*$/i.test(t)) return t;
  }
  const fn = user.firstName ?? user.first_name ?? "";
  const ln = user.lastName ?? user.last_name ?? "";
  const parts = [fn, ln].filter(
    (p) =>
      p != null &&
      String(p).trim() !== "" &&
      String(p).trim().toLowerCase() !== "null"
  );
  return parts.map((p) => String(p).trim()).join(" ").trim();
}

function resolveJoinLobbyName() {
  try {
    const fromUrl = syncJoinLobbyFromUrlSearch(window.location.search);
    if (fromUrl) return fromUrl;
  } catch {
    // ignore
  }
  try {
    const stored = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (stored && stored.trim()) return stored.trim();
  } catch {
    // ignore storage failures
  }
  return "";
}

export default function Lobby() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const storedUserData = useMemo(() => readStoredUserData(), []);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [joining, setJoining] = useState(false);

  const handleCreate = () => {
    const lobbyName = buildLobbyName();
    try {
      sessionStorage.setItem(CURRENT_LOBBY_KEY, lobbyName);
    } catch {
      // ignore storage write failures
    }
    navigate("/question", { state: { lobbyName } });
  };

  const handleNext = async () => {
    if (!pin.trim()) {
      setPinError("Please enter a PIN");
      return;
    }
    setPinError("");
    const userId =
      storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sessionId = storedUserData.sessionId;
    const organizationId = storedUserData.organizationId;
    const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
      /\/+$/,
      ""
    );

    if (!backendBase || !adminToken || !userId || !sessionId || !organizationId) {
      setPinError("Missing session details. Please login again.");
      return;
    }

    const playerName = normalizePlayerName(storedUserData);
    const roleForApi =
      storedUserData.user_type ||
      storedUserData.userType ||
      storedUserData.role ||
      (storedUserData.source === "DEMO" ? "demobypass" : null) ||
      storedUserData.source ||
      "ORG_USER";

    const pinTrim = pin.trim();
    const pinNum = Number(pinTrim);
    const loginPinBody =
      pinTrim !== "" && Number.isFinite(pinNum)
        ? { login_pin: Math.trunc(pinNum) }
        : {};
    const joinLobbyHint = resolveJoinLobbyName();
    let matchedLobbyName = "";

    try {
      setJoining(true);
      const res = await fetch(`${backendBase}/addContent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId,
          sessionId,
          organizationId,
          email: storedUserData.email || "",
          emp_id: storedUserData.employeeId || storedUserData.emp_id || "",
          name: playerName,
          reportId: storedUserData.reportId || storedUserData.reportid || "",
          gameId: storedUserData.gameId || "",
          token: storedUserData.token || "",
          user_type: roleForApi,
          ...(joinLobbyHint ? { lobby: joinLobbyHint } : {}),
          ...loginPinBody,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Could not initialize user.");
      }
      const joinedLobby =
        typeof data.lobby === "string" ? data.lobby.trim() : "";
      if (!joinedLobby) {
        await Swal.fire({
          icon: "error",
          text: "Wrong lobby pin",
          confirmButtonText: "OK",
        });
        setJoining(false);
        return;
      }
      matchedLobbyName = joinedLobby;
      try {
        sessionStorage.setItem(CURRENT_LOBBY_KEY, matchedLobbyName);
      } catch {
        // ignore
      }
      persistUserDataIds({
        lobby: matchedLobbyName,
        stageId: data.id,
        reportId: data.reportId,
      });
    } catch (err) {
      const msg = String(err?.message || "").trim();
      if (!msg || /pin|lobby|join/i.test(msg)) {
        await Swal.fire({
          icon: "error",
          text: "Wrong lobby pin",
          confirmButtonText: "OK",
        });
      } else {
        setPinError(msg || "Could not join game.");
      }
      setJoining(false);
      return;
    }

    setJoining(false);
    navigate("/game", { state: { pin, lobbyName: matchedLobbyName } });
  };

  useEffect(() => {
    document.documentElement.classList.add("lobby-static-bg");
    document.body.classList.add("lobby-static-bg");
    return () => {
      document.documentElement.classList.remove("lobby-static-bg");
      document.body.classList.remove("lobby-static-bg");
    };
  }, []);

  return (
    <div className="page">
    

      <h1 className="heading">LOBBY</h1>

      <div className="cardsRow">

        {/* ── CREATE CARD ── */}
        <div className="lobbyCard createCard">
          <h2 className="cardTitle">CREATE</h2>
          <div className="cardInner createInner">
            <button className="createBtn" onClick={handleCreate}>
              Create
            </button>
          </div>
        </div>

        {/* ── JOIN CARD ── */}
        <div className="lobbyCard joinCard">
          <h2 className="cardTitle">JOIN</h2>
          <div className="cardInner joinInner">
            <div className="fullWidth">
              <input
                className="joinInput"
                type="text"
                placeholder="Enter PIN"
                value={pin}
                maxLength={8}
                onChange={(e) => {
                  setPin(e.target.value);
                  setPinError("");
                }}
              />
              {pinError && <p className="errorText">{pinError}</p>}
            </div>
          </div>
        </div>

      </div>

      <button className="nextBtn" onClick={handleNext} disabled={joining}>
        {joining ? "Joining..." : "Next"}
      </button>
    </div>
  );
}
